import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { FormEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatListItemComponent } from '../components/chats-list/chats-list-item/chats-list-item.component';
import { ChatsListComponent } from '../components/chats-list/chats-list.component';
import { ContainerComponent } from '../components/container/container.component';
import { ResizableSplitLayoutComponent } from '../components/resizable-split-layout/resizable-split-layout.component';
import { useRequest } from '../hooks/useRequest';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { refreshPresets } from '../store/slices/presets-slice';
import {
  createPreset,
  deletePreset,
  Preset,
  updatePreset,
} from '../utils/axios/api';

export function PresetsPage() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const presets = useAppSelector((state) => state.presets.items);
  const saveRequest = useRequest<Preset>();
  const deleteRequest = useRequest();
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [text, setText] = useState('');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const resetForm = useCallback(() => {
    setSelectedPresetId(null);
    setName('');
    setText('');
    setValidationError(null);
    setIsDeleteDialogOpen(false);
  }, []);

  const handleNewPreset = useCallback(() => {
    resetForm();
  }, [resetForm]);

  const handleSelectPreset = useCallback((preset: Preset) => {
    setSelectedPresetId(preset.id);
    setName(preset.name);
    setText(preset.text);
    setValidationError(null);
    setIsDeleteDialogOpen(false);
  }, []);

  const handleDelete = useCallback(async () => {
    if (!selectedPresetId) {
      return;
    }

    setValidationError(null);

    const result = await deleteRequest.fetch(async () => {
      await deletePreset(selectedPresetId);
      await dispatch(refreshPresets()).unwrap();
    });
    if (result === null) {
      setIsDeleteDialogOpen(false);
      return;
    }
    resetForm();
  }, [deleteRequest, dispatch, resetForm, selectedPresetId]);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const trimmedName = name.trim();
      const trimmedText = text.trim();

      if (!trimmedName || !trimmedText) {
        setValidationError(t('presets.errors.required'));
        return;
      }

      setValidationError(null);

      if (selectedPresetId) {
        const updated = await saveRequest.fetch(async () => {
          const result = await updatePreset(selectedPresetId, {
            name: trimmedName,
            text: trimmedText,
          });
          await dispatch(refreshPresets()).unwrap();
          return result;
        });
        if (!updated) {
          return;
        }
        setName(updated.name);
        setText(updated.text);
        return
      }
      const created = await saveRequest.fetch(async () => {
        const result = await createPreset({
          name: trimmedName,
          text: trimmedText,
        });
        await dispatch(refreshPresets()).unwrap();
        return result;
      });
      if (!created) {
        return;
      }
      setSelectedPresetId(created.id);
      setName(created.name);
      setText(created.text);

    },
    [dispatch, name, saveRequest, selectedPresetId, t, text],
  );

  return (
    <ResizableSplitLayoutComponent
      left={
        <ChatsListComponent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Typography variant="h5">{t('presets.title')}</Typography>
            <Tooltip title={t('presets.new')}>
              <IconButton
                size="small"
                aria-label={t('presets.new')}
                onClick={handleNewPreset}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {presets.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('presets.empty')}
            </Typography>
          ) : (
            presets.map((preset) => (
              <ChatListItemComponent
                key={preset.id}
                chat={{
                  id: preset.id,
                  name: preset.name,
                  userId: preset.userId,
                  presetId: null,
                  createdAt: preset.createdAt,
                  updatedAt: preset.updatedAt,
                }}
                selected={preset.id === selectedPresetId}
                onClick={() => {
                  handleSelectPreset(preset);
                }}
              />
            ))
          )}
        </ChatsListComponent>
      }
      right={
        <ContainerComponent>
          <Box
            component="form"
            onSubmit={(event) => {
              void handleSubmit(event);
            }}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              flex: 1,
              p: 2,
            }}
          >
            <Typography variant="h6">
              {selectedPresetId ? t('presets.edit') : t('presets.create')}
            </Typography>
            <TextField
              label={t('presets.name')}
              value={name}
              onChange={(event) => {
                setName(event.target.value);
              }}
              fullWidth
              required
              disabled={saveRequest.isLoading}
            />
            <TextField
              label={t('presets.text')}
              value={text}
              onChange={(event) => {
                setText(event.target.value);
              }}
              fullWidth
              required
              multiline
              minRows={8}
              disabled={saveRequest.isLoading}
              sx={{ flex: 1 }}
            />
            {validationError ??
              (deleteRequest.exception ?? saveRequest.exception) ? (
              <Typography variant="body2" color="error">
                {validationError ??
                  t(
                    `exceptions.${deleteRequest.exception ?? saveRequest.exception}`,
                  )}
              </Typography>
            ) : null}
            <Box
              sx={{
                display: 'flex',
                justifyContent: selectedPresetId ? 'space-between' : 'flex-end',
                gap: 1,
              }}
            >
              {selectedPresetId ? (
                <Button
                  color="error"
                  variant="outlined"
                  disabled={saveRequest.isLoading || deleteRequest.isLoading}
                  onClick={() => setIsDeleteDialogOpen(true)}
                >
                  {t('presets.delete.button')}
                </Button>
              ) : null}
              <Button
                type="submit"
                variant="contained"
                disabled={saveRequest.isLoading || deleteRequest.isLoading}
              >
                {saveRequest.isLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : selectedPresetId ? (
                  t('presets.save')
                ) : (
                  t('presets.createSubmit')
                )}
              </Button>
            </Box>
          </Box>
          <Dialog
            open={isDeleteDialogOpen}
            onClose={() => {
              if (!deleteRequest.isLoading) {
                setIsDeleteDialogOpen(false);
              }
            }}
          >
            <DialogTitle>{t('presets.delete.title')}</DialogTitle>
            <DialogContent>
              <DialogContentText>
                {t('presets.delete.confirm', { name })}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setIsDeleteDialogOpen(false)}
                disabled={deleteRequest.isLoading}
              >
                {t('presets.delete.cancel')}
              </Button>
              <Button
                color="error"
                variant="contained"
                disabled={deleteRequest.isLoading}
                onClick={() => {
                  void handleDelete();
                }}
              >
                {deleteRequest.isLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  t('presets.delete.button')
                )}
              </Button>
            </DialogActions>
          </Dialog>
        </ContainerComponent>
      }
    />
  );
}
